import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { projects } from "../data/projects";
import { caseStudies } from "../data/caseStudies";
import { DISPLAY_HEADING, EYEBROW, H2, LEAD } from "../utils/typography";
import { TreatedImage } from "../components/TreatedImage";
import { ImagePlaceholder } from "../components/caseStudy/ImagePlaceholder";
import { ChallengeSolution } from "../components/caseStudy/ChallengeSolution";
import { ResultsSection } from "../components/caseStudy/ResultsSection";
import { ProcessSection } from "../components/caseStudy/ProcessSection";
import { KeyDecisions } from "../components/caseStudy/KeyDecisions";
import { BeforeAfter } from "../components/caseStudy/BeforeAfter";
import { ReflectionSection } from "../components/caseStudy/ReflectionSection";
import { GoDeeper } from "../components/caseStudy/GoDeeper";
import { RelatedThoughts } from "../components/caseStudy/RelatedThoughts";
import { Reveal } from "../components/Reveal";
import { usePageTitle } from "../hooks/usePageTitle";
export function CaseStudy() {
  const {
    id
  } = useParams();
  const caseStudyProjects = projects.filter(p => p.kind === "case-study");
  const index = caseStudyProjects.findIndex(p => p.id === id);
  const project = caseStudyProjects[index];
  const content = project ? caseStudies[project.id] : undefined;
  usePageTitle(project && content ? project.title : "Project not found");
  if (!project || !content) {
    return <main id="main-content" tabIndex={-1} className="flex min-h-screen w-full flex-col items-start justify-center bg-cream px-6 md:px-12 lg:px-20" data-id="element-348" data-mp="2o8mi21g8w50g">
        <p className="text-sm font-medium uppercase tracking-wide text-rust" data-id="element-349" data-mp="2x7zsoxg3uzmr">Not found</p>
        <h1 className={`mt-4 ${DISPLAY_HEADING} text-espresso`} data-id="element-350" data-mp="hglogxditsdc">
          This project isn’t here.
        </h1>
        <Link to="/#projects" className="mt-10 inline-flex items-center gap-2 rounded-full bg-espresso px-6 py-3 text-sm font-medium uppercase text-cream transition-colors duration-150 hover:bg-rust" data-id="element-351" data-mp="chr6fxndo7ua">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-352" data-mp="3b78xxmcwc3ad" />
          All projects
        </Link>
      </main>;
  }

  // Follow homepage order, looping from the last case study back to the first
  const next = caseStudyProjects[(index + 1) % caseStudyProjects.length];
  const nextLabel = `Next project / ${caseStudies[next.id]?.number ?? ""}`;
  return <main id="main-content" tabIndex={-1} className="w-full bg-cream pb-24 pt-36 md:pb-32 md:pt-48 lg:pb-40" data-id="element-353" data-mp="2nh6axknu9nim">
      <article data-id="element-354" data-mp="2wjxmrlkmmlas">
        {/* Title */}
        <Reveal className="px-6 md:px-12 lg:px-20" data-id="element-355" data-mp="2wivygfelo2b4">
        <header data-id="element-356" data-mp="31adz12cpo0vk">
          <Link to="/#projects" className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-espresso/70 transition-colors duration-150 hover:text-rust" data-id="element-357" data-mp="31axk2rxnr5dt">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-358" data-mp="28t1nk3ducnti" />
            All projects
          </Link>
          <p className="mt-10 text-sm font-medium uppercase tracking-wide text-rust" data-id="element-359" data-mp="21au39ij6rc56">
            Case study / {content.number} · {project.client}
          </p>
          <h1 className={`mt-4 max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} data-id="element-360" data-mp="1w9o9rihpbkul">
            {project.title}
          </h1>
        </header>
        </Reveal>

        {/* Hero image */}
        <Reveal className="mt-12 px-6 md:mt-16 md:px-12 lg:px-20" delay={0.06} data-id="element-361" data-mp="1ncvpofcovjqq">
          {project.image ? <TreatedImage src={project.image} alt={`${project.title} — ${project.client}`} className="aspect-[16/9] w-full rounded-3xl bg-paper" motionMask={project.motionMask} motionStyle={project.motionStyle} eager data-id="element-362" data-mp="1idj5619aiorb" /> : <ImagePlaceholder caption="Hero image" className="aspect-[16/9] w-full" data-id="element-363" data-mp="zlq8nwv0ileu" />}
        </Reveal>

        <div className="px-6 md:px-12 lg:px-20" data-id="element-364" data-mp="un6ljt4on3kw">
          {/* Lede + facts */}
          <Reveal className="mt-16 grid grid-cols-1 gap-12 md:mt-24 lg:grid-cols-12" data-id="element-365" data-mp="2unv3pywou19o">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-6 self-start lg:col-span-4 lg:grid-cols-1" data-id="element-366" data-mp="2ypqf5w8c1v16">
              {content.facts.map(fact => <div key={fact.label} data-id="element-367" data-mp="2ky231sy336k7">
                  <dt className={`${EYEBROW} text-espresso/70`} data-id="element-368" data-mp="2pxof3z26egrf">
                    {fact.label}
                  </dt>
                  <dd className="mt-1 text-espresso" data-id="element-369" data-mp="2azw1xdk3q4ge">{fact.value}</dd>
                </div>)}
            </dl>
            <p className={`${LEAD} text-espresso lg:col-span-8`} data-id="element-370" data-mp="2g0a083vwi018">
              {content.lede}
            </p>
          </Reveal>

          <div className="mt-24 space-y-24 md:mt-32 md:space-y-32 lg:mt-40 lg:space-y-40" data-id="element-371" data-mp="221sh6jgxxbpn">
            <Reveal data-id="element-372" data-mp="26z2d8hyu4lmg">
              <ChallengeSolution challenge={content.challenge} solution={content.solution} data-id="element-373" data-mp="270n7s7l5g4kb" />
            </Reveal>
            <Reveal data-id="element-374" data-mp="575sqiuow1fj">
              <ResultsSection results={content.results} data-id="element-375" data-mp="57pd067tb1wq" />
            </Reveal>
            <Reveal data-id="element-376" data-mp="fb4dwk15ncdj">
              <ProcessSection process={content.process} data-id="element-377" data-mp="fakxj7r5kw1m" />
            </Reveal>
            <Reveal data-id="element-378" data-mp="acje2lpteupm">
              <KeyDecisions decisions={content.decisions} data-id="element-379" data-mp="ad2mnbtks6qz" />
            </Reveal>
            {content.beforeAfter && <Reveal data-id="element-380" data-mp="o4hco2lwh87j">
                <BeforeAfter panels={content.beforeAfter} data-id="element-381" data-mp="o5skcdigv03x" />
              </Reveal>}
            <Reveal data-id="element-382" data-mp="jav00afql4bq">
              <ReflectionSection learned={content.learned} data-id="element-383" data-mp="y1vjrblxokn6" />
            </Reveal>
            <Reveal data-id="element-384" data-mp="y3yhkhtbvi2g">
              <RelatedThoughts slugs={content.relatedThoughts} data-id="element-385" data-mp="t2r88ebjeato" />
            </Reveal>
            <Reveal data-id="element-386" data-mp="t552wm2b1l0p">
              <GoDeeper deeper={content.deeper} data-id="element-387" data-mp="172a68eamw5s9" />
            </Reveal>
          </div>
        </div>
      </article>

      {/* Next project */}
      {next && <Reveal data-id="element-388" data-mp="131qddmx3fobx">
        <nav aria-label="Next project" className="mx-6 mt-24 md:mx-12 md:mt-32 lg:mx-20 lg:mt-40" data-id="element-389" data-mp="12z44njygv2s4">
          <Link to={next.href} className="group flex items-end justify-between gap-6" data-id="element-390" data-mp="38vq7s689f520">
            <div data-id="element-391" data-mp="34ueiy2x7mnuv">
              <p className="text-sm font-medium uppercase tracking-wide text-espresso/70" data-id="element-392" data-mp="17rp6lwe4a8en">
                {nextLabel}
              </p>
              <p className={`mt-3 ${H2} text-espresso transition-colors duration-150 group-hover:text-rust`} data-id="element-393" data-mp="1crtnhnixt55e">
                {next.title}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-394" data-mp="1moohbt49mhdc">
              <ArrowUpRightIcon className="h-5 w-5" data-id="element-395" data-mp="1qopa6is42v3x" />
            </span>
          </Link>
        </nav>
        </Reveal>}
    </main>;
}